// 家用面板「打給家人」的網頁端（docs/panel/08-call-family.md 方案 B；John 2026-10-22：網頁也要能打給面板、接面板打來的電話）。
//   - 來電：signaling call_ring（direction 沒有或 to_family）馬上響 + 前景每 5 秒 GET /v3/me/calls 補漏（同 Android CALL_POLL_MS）；
//     POST /v3/me/calls:answer|:decline|:end {call_id}
//   - 打給面板：POST /v3/me/panel-calls {panel_device_id} → call；GET /v3/me/panel-calls/{call_id} 可輪詢；
//     signaling call_answered {by_name, auto} → 接通、call_declined / call_end → 結束；30 秒沒接 → 沒有接
//   - WebRTC 角色照既有「打給家人」：**手機 / 網頁 = offerer、面板 = answerer**（不論誰撥的）：送 call_join → call_ready {ice_servers}
//     → 加本機鏡頭 / 麥克風 → createOffer → setLocal → 送 call_offer；收 call_answer → setRemote；ICE 走 call_ice。SDP / ICE 不寫 log。
// 狀態與畫面：panelCall.ts（store）、PanelCallOverlay.tsx。
import { apiFetch } from '../../lib/api';
import type { SignalFrame } from '../../lib/ws';

export const CALL_POLL_MS = 5_000;
/** 響鈴 30 秒（雲端 / 面板同一個數） */
export const CALL_RING_S = 30;

export interface PanelCall {
  call_id: string;
  panel_device_id: string;
  panel_name: string;
  /** 雲端以「我」算好的來電名字（我的叫法 → 打電話時顯示的名字 → 面板名稱）；舊雲端沒有 → 不帶 */
  caller_name?: string;
  state: string;
  /** unix 秒；0 = 不知道 */
  at: number;
  /** sos = 面板按了緊急求救後自動撥的 */
  reason: string;
}

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

export function parsePanelCall(raw: unknown): PanelCall | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o.call_id);
  const pid = str(o.panel_device_id);
  if (!id || !pid) return null;
  const caller = str(o.caller_name);
  return {
    call_id: id,
    panel_device_id: pid,
    panel_name: str(o.panel_name),
    ...(caller ? { caller_name: caller } : {}),
    state: str(o.state) || 'ringing',
    at: typeof o.at === 'number' && Number.isFinite(o.at) ? o.at : 0,
    reason: str(o.reason),
  };
}

export function parsePanelCallList(raw: unknown): PanelCall[] {
  const list = (raw as { calls?: unknown } | null)?.calls;
  if (!Array.isArray(list)) return [];
  return list.flatMap((v) => {
    const c = parsePanelCall(v);
    return c ? [c] : [];
  });
}

/** 來電大字：caller_name，沒有 → 面板名稱 */
export function panelCallTitle(c: Pick<PanelCall, 'caller_name' | 'panel_name'>): string {
  return c.caller_name || c.panel_name;
}

/** 來電小字：跟大字不同時才顯示面板名稱 */
export function panelCallSubtitle(c: Pick<PanelCall, 'caller_name' | 'panel_name'>): string | null {
  const title = panelCallTitle(c);
  return c.panel_name && c.panel_name !== title ? c.panel_name : null;
}

/** signaling call_ring 是不是打給我（家人）的：direction 沒有（舊雲端）或 to_family；網頁打給面板的那個方向不響 */
export function isIncomingRing(f: SignalFrame): boolean {
  if (f.type !== 'call_ring') return false;
  const d = f.direction ?? (f.payload as Record<string, unknown> | undefined)?.direction;
  return d === undefined || d === null || d === '' || d === 'to_family';
}

/** call_ring 的欄位可能在頂層或 payload 裡 */
export function callOfFrame(f: SignalFrame): PanelCall | null {
  return parsePanelCall({ ...(f.payload ?? {}), ...f, state: 'ringing' });
}

/** 要不要跳來電：還在響、沒超過 30 秒、不是處理過的（拒接 / 接過） */
export function shouldRing(c: PanelCall, handled: ReadonlySet<string>, nowS: number): boolean {
  return c.state === 'ringing' && !handled.has(c.call_id) && (c.at === 0 || nowS - c.at <= CALL_RING_S);
}

// ---- REST ----

export async function listMyCalls(): Promise<PanelCall[]> {
  return parsePanelCallList((await apiFetch<unknown>('/v3/me/calls')).data);
}

export type CallAction = 'answer' | 'decline' | 'end';

export async function callAction(callId: string, action: CallAction): Promise<void> {
  await apiFetch(`/v3/me/calls:${action}`, { method: 'POST', body: { call_id: callId } });
}

/** 打給這台面板（家人 / 主人）。回應缺 panel_device_id 時用送出的那台補 */
export async function startPanelCall(panelId: string): Promise<PanelCall> {
  const r = (await apiFetch<unknown>('/v3/me/panel-calls', { method: 'POST', body: { panel_device_id: panelId } })).data;
  const c = parsePanelCall({ panel_device_id: panelId, ...(r && typeof r === 'object' ? r : {}) });
  if (!c) throw new Error('bad call');
  return c;
}

export async function getPanelCall(callId: string): Promise<PanelCall | null> {
  return parsePanelCall((await apiFetch<unknown>(`/v3/me/panel-calls/${encodeURIComponent(callId)}`)).data);
}

/** 打出去的這通，輪詢到的狀態代表什麼 */
export function outgoingStateOutcome(state: string): 'ringing' | 'answered' | 'declined' | 'ended' {
  if (state === 'answered' || state === 'in_call' || state === 'connected') return 'answered';
  if (state === 'declined' || state === 'rejected') return 'declined';
  if (state === 'ringing' || state === 'calling' || state === '') return 'ringing';
  return 'ended';
}

// ---- WebRTC（網頁 = offerer）----

/** ice_servers（[{urls, username, credential}]）→ RTCIceServer；壞的丟掉 */
export function iceServersOf(raw: unknown): RTCIceServer[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((v) => {
    const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    const u = o.urls ?? o.url;
    const urls = typeof u === 'string' ? [u] : Array.isArray(u) ? u.filter((x): x is string => typeof x === 'string') : [];
    if (!urls.length) return [];
    const s: RTCIceServer = { urls };
    if (typeof o.username === 'string') s.username = o.username;
    const cred = o.credential ?? o.password;
    if (typeof cred === 'string') s.credential = cred;
    return [s];
  });
}

const sdpOf = (f: SignalFrame): string | null => {
  const v = f.sdp as unknown;
  const s = typeof v === 'string' ? v : v && typeof v === 'object' ? (v as { sdp?: unknown }).sdp : null;
  return typeof s === 'string' && s ? s : null;
};

export interface CallPeerDeps {
  callId: string;
  send: (f: SignalFrame) => boolean;
  /** 本機鏡頭 + 麥克風（接聽 / 撥出時已經拿到；拿不到 = null，只看對方） */
  local: MediaStream | null;
  onRemote: (s: MediaStream) => void;
  onConnected: () => void;
  onFailed: (why: string) => void;
  createPc?: (cfg: RTCConfiguration) => RTCPeerConnection;
}

/** 一通的 WebRTC（網頁 = offerer，對齊 Android lslive CallPeer OFFERER）：start() 送 call_join；handle() 吃 call_ready / call_answer / call_ice / error */
export class CallPeer {
  private pc: RTCPeerConnection | null = null;
  private pendingIce: RTCIceCandidateInit[] = [];
  private remoteSet = false;
  private stopped = false;
  private connected = false;
  private remote: MediaStream | null = null;

  constructor(private readonly d: CallPeerDeps) {}

  start(): void {
    if (!this.d.send({ type: 'call_join', call_id: this.d.callId })) this.d.onFailed('signaling offline');
  }

  handle(f: SignalFrame): void {
    if (this.stopped || f.call_id !== this.d.callId) return;
    switch (f.type) {
      case 'call_ready':
        void this.setUp(iceServersOf(f.ice_servers ?? (f.payload as Record<string, unknown> | undefined)?.ice_servers));
        break;
      case 'call_answer': {
        const sdp = sdpOf(f);
        if (!sdp || !this.pc || this.remoteSet) return;
        void this.pc.setRemoteDescription({ type: 'answer', sdp }).then(() => {
          if (this.stopped || !this.pc) return;
          this.remoteSet = true;
          for (const c of this.pendingIce.splice(0)) void this.pc.addIceCandidate(c).catch(() => {});
        }).catch((e: unknown) => { if (!this.stopped) this.d.onFailed(`answer ${e instanceof Error ? e.name : 'error'}`); });
        break;
      }
      case 'call_ice': {
        const c = f.candidate as Record<string, unknown> | undefined;
        if (!c || typeof c.candidate !== 'string') return;
        const ice: RTCIceCandidateInit = {
          candidate: c.candidate,
          sdpMid: typeof c.sdpMid === 'string' ? c.sdpMid : null,
          sdpMLineIndex: typeof c.sdpMLineIndex === 'number' ? c.sdpMLineIndex : null,
        };
        if (this.remoteSet && this.pc) void this.pc.addIceCandidate(ice).catch(() => {});
        else this.pendingIce.push(ice);
        break;
      }
      case 'error':
        this.d.onFailed(typeof f.message === 'string' ? f.message : 'error');
        break;
    }
  }

  /** call_ready → PeerConnection（雲端給的 ice_servers）→ 本機軌道（沒有的種類改只收）→ createOffer → setLocal → 送 call_offer */
  private async setUp(iceServers: RTCIceServer[]): Promise<void> {
    if (this.pc) return;
    const make = this.d.createPc ?? ((cfg: RTCConfiguration) => new RTCPeerConnection(cfg));
    const pc = make({ iceServers });
    this.pc = pc;
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      this.d.send({ type: 'call_ice', call_id: this.d.callId,
        candidate: { candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid, sdpMLineIndex: e.candidate.sdpMLineIndex } });
    };
    pc.ontrack = (e) => {
      if (this.stopped) return;
      const s = e.streams[0] ?? (this.remote ??= new MediaStream());
      if (!e.streams[0]) s.addTrack(e.track);
      this.remote = s;
      this.d.onRemote(s);
    };
    pc.oniceconnectionstatechange = () => {
      if (this.stopped) return;
      const st = pc.iceConnectionState;
      if ((st === 'connected' || st === 'completed') && !this.connected) {
        this.connected = true;
        this.d.onConnected();
      } else if (st === 'failed') this.d.onFailed('ice failed');
    };
    const local = this.d.local;
    for (const kind of ['audio', 'video'] as const) {
      const t = local?.getTracks().find((x) => x.kind === kind);
      if (t && local) pc.addTrack(t, local);
      else pc.addTransceiver(kind, { direction: 'recvonly' });   // 沒鏡頭 / 沒給權限 → 還是要收面板的畫面與聲音
    }
    try {
      const offer = await pc.createOffer();
      if (this.stopped) return;
      await pc.setLocalDescription(offer);
      if (this.stopped) return;
      this.d.send({ type: 'call_offer', call_id: this.d.callId, sdp: { type: 'offer', sdp: offer.sdp ?? '' } });
    } catch (e) {
      if (!this.stopped) this.d.onFailed(`offer ${e instanceof Error ? e.name : 'error'}`);
    }
  }

  setMuted(muted: boolean): void {
    this.d.local?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    try {
      this.pc?.close();
    } catch {
      /* 已經關了 */
    }
    this.pc = null;
  }
}
